import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { Icons, notify, Stack, Typography } from '@cast/design-system';
import {
  MutateWorkloadScalingPolicy,
  WorkloadOptimizationState,
  WorkloadScalingPolicy,
} from '@cast/types';

import {
  useBatchCreateScalingPoliciesMutation,
  useBatchUpdateScalingPoliciesMutation,
  useUpdateWorkloadScalingPolicyOrderMutation,
} from '@cast/split-me/hooks/mutations/workload-optimization';
import { getWorkloadScalingPoliciesQueryKey } from '@cast/split-me/hooks/queries/workload-optimization';

import { DEFAULT_EXCLUDED_NAMESPACES } from '../_constants';
import { ApplyState, RecommendationsState } from '../_types';
import {
  EnvironmentType,
  OptimizationGoal,
  PolicyOrderEntry,
  WrappedPolicy,
} from '../open-api-types/autopilot-types';
import { useAutoPilotA2A } from './useAutoPilotA2A';

// Builds the complete ordered policy ID list required by the order API.
// cachedPolicies is the authoritative source; policyOrder drives sequence but may have
// missing or extra names — unmatched are ignored, unordered policies appended at the end.
function buildOrderedPolicyIds(
  policyOrder: PolicyOrderEntry[],
  cachedPolicies: WorkloadScalingPolicy[] | null | undefined
): string[] {
  const policies = cachedPolicies ?? [];
  const orderedNames = new Set(policyOrder.map((e) => e.name));

  const ordered = policyOrder
    .map((e) => policies.find((p) => p.name === e.name)?.id)
    .filter((id): id is string => Boolean(id));

  const remaining = policies
    .filter((p) => p.name && !orderedNames.has(p.name))
    .map((p) => p.id)
    .filter((id): id is string => Boolean(id));

  return [...new Set([...ordered, ...remaining])];
}

type UseWorkloadAutoscalerProps = {
  clusterName: string;
  preferences: {
    priority: OptimizationGoal | null;
    clusterUsage: EnvironmentType | null;
  };
  excludedNamespaces: string[];
  recommendations: RecommendationsState;
  setRecommendations: (rec: RecommendationsState) => void;
  onGenerateSuccess?: () => void;
  updateApplyState: (update: Partial<ApplyState>) => void;
};

const INITIAL_RECOMMENDATIONS: RecommendationsState = {
  data: null,
  isLoading: false,
  error: null,
};

export const useWorkloadAutoscaler = ({
  clusterName,
  preferences,
  excludedNamespaces,
  recommendations,
  setRecommendations,
  onGenerateSuccess,
  updateApplyState,
}: UseWorkloadAutoscalerProps) => {
  const { generate: a2aGenerate, abort } = useAutoPilotA2A();
  const queryClient = useQueryClient();
  const batchCreateMutation = useBatchCreateScalingPoliciesMutation();
  const batchUpdateMutation = useBatchUpdateScalingPoliciesMutation();
  const orderMutation = useUpdateWorkloadScalingPolicyOrderMutation({
    notifyOnSuccess: false,
  });

  const generate = useCallback(async () => {
    try {
      const recommendation = await a2aGenerate({
        clusterName,
        priority: preferences.priority!,
        environmentType: preferences.clusterUsage!,
        excludedNamespaces: [
          ...DEFAULT_EXCLUDED_NAMESPACES,
          ...excludedNamespaces,
        ],
      });
      setRecommendations({
        data: recommendation,
        isLoading: false,
        error: null,
      });
      onGenerateSuccess?.();
      notify.success(
        <Stack direction="row" alignItems="center" gap={8}>
          <Icons.Sparkle size={18} weight="fill" />
          <Stack gap={2}>
            <Typography variant="P14B" color="inherit">
              OpsPilot complete
            </Typography>
            <Typography variant="P12R" color="inherit" sx={{ opacity: 0.8 }}>
              Your recommendations are ready to review
            </Typography>
          </Stack>
        </Stack>
      );
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to generate recommendations. Please try again.';
      setRecommendations({
        ...recommendations,
        isLoading: false,
        error: message,
      });
    }
  }, [
    clusterName,
    preferences,
    excludedNamespaces,
    recommendations,
    a2aGenerate,
    setRecommendations,
    onGenerateSuccess,
  ]);

  const cancel = useCallback(() => {
    abort();
    setRecommendations(INITIAL_RECOMMENDATIONS);
  }, [abort, setRecommendations]);

  const onUpdatePolicyManagementOption = useCallback(
    (policyName: string, managementOption: WorkloadOptimizationState) => {
      const data = recommendations.data;
      if (!data) return;

      const updatePolicy = (wp: WrappedPolicy): WrappedPolicy => {
        if (wp.payload.name !== policyName) return wp;
        return {
          ...wp,
          payload: {
            ...wp.payload,
            recommendationPolicies: {
              ...wp.payload.recommendationPolicies,
              managementOption,
            },
          },
        };
      };

      setRecommendations({
        ...recommendations,
        data: {
          ...data,
          newPolicies: data.newPolicies?.map(updatePolicy),
          updatePolicies: data.updatePolicies?.map(updatePolicy),
        },
      });
    },
    [recommendations, setRecommendations]
  );

  const runWorkloadPolicies = useCallback(
    async (applyState: ApplyState): Promise<boolean> => {
      const recommendation = recommendations.data;
      if (!recommendation) return true;

      const {
        clusterId,
        newPolicies = [],
        updatePolicies = [],
        policyOrder = [],
      } = recommendation;

      // Step 1: Create new policies
      if (applyState.currentStep === 'create') {
        updateApplyState({ error: null });

        const createdPolicyIds = { ...applyState.createdPolicyIds };

        if (newPolicies?.length) {
          const toCreate = newPolicies
            .filter((wp) => !(wp.payload.name in applyState.createdPolicyIds))
            .map((wp) => ({
              clusterId,
              policy: {
                ...wp.payload,
                recommendationPolicies: {
                  ...wp.payload.recommendationPolicies,
                  managementOption: WorkloadOptimizationState.READ_ONLY,
                },
                isOpsPilot: true,
              } satisfies MutateWorkloadScalingPolicy,
            }));

          const results = await batchCreateMutation.mutateAsync(toCreate);

          results.forEach((r, i) => {
            if (r.status === 'fulfilled') {
              const { name } = toCreate[i].policy;
              const { id } = r.value.data;
              if (name && id) createdPolicyIds[name] = id;
            }
          });

          if (results.some((r) => r.status === 'rejected')) {
            updateApplyState({
              createdPolicyIds,
              error: 'Failed to create some policies.',
            });
            return false;
          }
        }

        updateApplyState({ createdPolicyIds, currentStep: 'update' });
        applyState = { ...applyState, createdPolicyIds, currentStep: 'update' };
      }

      // Step 2: Update existing policies
      if (applyState.currentStep === 'update') {
        updateApplyState({ error: null });

        if (updatePolicies?.length) {
          const results = await batchUpdateMutation.mutateAsync({
            policies: updatePolicies.map((wp) => ({
              id: wp.policyId!,
              clusterId,
              ...wp.payload,
              recommendationPolicies: {
                ...wp.payload.recommendationPolicies,
                managementOption: WorkloadOptimizationState.READ_ONLY,
              },
            })) as WorkloadScalingPolicy[],
            transformPolicy: (p) => p,
          });

          if (results.some((r) => r.status === 'rejected')) {
            updateApplyState({ error: 'Failed to update some policies.' });
            return false;
          }
        }

        updateApplyState({ updatesCompleted: true, currentStep: 'order' });
        applyState = { ...applyState, currentStep: 'order' };
      }

      // Step 3: Set policy order
      if (applyState.currentStep === 'order') {
        updateApplyState({ error: null });

        if (policyOrder?.length) {
          await queryClient.refetchQueries({
            queryKey: getWorkloadScalingPoliciesQueryKey({ clusterId }),
          });

          const cachedPolicies = queryClient.getQueryData<
            WorkloadScalingPolicy[]
          >(getWorkloadScalingPoliciesQueryKey({ clusterId }));

          const policyIds = buildOrderedPolicyIds(policyOrder, cachedPolicies);

          try {
            await orderMutation.mutateAsync({ clusterId, policyIds });
          } catch {
            updateApplyState({ error: 'Failed to set policy order.' });
            return false;
          }
        }
        updateApplyState({ orderCompleted: true });
      }

      return true;
    },
    [
      recommendations.data,
      queryClient,
      batchCreateMutation,
      batchUpdateMutation,
      orderMutation,
      updateApplyState,
    ]
  );

  return {
    generate,
    abort,
    cancel,
    onUpdatePolicyManagementOption,
    runWorkloadPolicies,
  };
};
